/**
 * Notification Preferences Utility
 *
 * Manages user notification preferences with localStorage persistence.
 * Controls when toast notifications should be displayed.
 */

export interface NotificationPreferences {
  /** Master switch for all notifications */
  notificationsEnabled: boolean;
  /** Specific toggle for alarm notifications from Firebase */
  alarmNotificationsEnabled: boolean;
  /** Toggle for newsletter subscription */
  newsletterEnabled?: boolean;
}

const STORAGE_KEY = 'notificationPreferences';

/**
 * Get current notification preferences from localStorage
 * @returns NotificationPreferences object with defaults if not found
 */
export function getNotificationPreferences(): NotificationPreferences {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Ensure all required fields exist
      return {
        notificationsEnabled: parsed.notificationsEnabled ?? true,
        alarmNotificationsEnabled: parsed.alarmNotificationsEnabled ?? true,
        newsletterEnabled: parsed.newsletterEnabled ?? false,
      };
    }
  } catch (error) {
    console.error('[NotificationPreferences] Error reading preferences:', error);
  }

  // Default: notifications enabled, newsletter disabled
  return {
    notificationsEnabled: true,
    alarmNotificationsEnabled: true,
    newsletterEnabled: false,
  };
}

/**
 * Save notification preferences to localStorage
 * @param preferences - NotificationPreferences object to save
 */
export function saveNotificationPreferences(preferences: NotificationPreferences): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    console.log('[NotificationPreferences] Preferences saved:', preferences);
  } catch (error) {
    console.error('[NotificationPreferences] Error saving preferences:', error);
  }
}

/**
 * Check if a notification payload is an alarm notification
 * Alarm notifications come from Firebase FCM and have specific fields
 * @param payload - Any notification payload object
 * @returns true if this is an alarm notification from FCM
 */
export function isAlarmNotification(payload: any): boolean {
  // FCM alarm notifications have category or deviceId fields
  return !!(payload.category || payload.deviceId || payload.alarmId);
}

/**
 * Check if a notification should be displayed based on user preferences
 * @param isAlarm - Whether this is an alarm notification
 * @param bypassSettings - If true, always show regardless of settings
 * @returns true if notification should be shown
 */
export function shouldShowNotification(isAlarm: boolean, bypassSettings: boolean = false): boolean {
  if (bypassSettings) {
    return true;
  }

  const prefs = getNotificationPreferences();

  // If all notifications are disabled, don't show anything
  if (!prefs.notificationsEnabled) {
    return false;
  }

  // If this is an alarm notification, check alarm-specific setting
  if (isAlarm && !prefs.alarmNotificationsEnabled) {
    return false;
  }

  return true;
}
